import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Waveform, Translate, FileText, CloudArrowUp, Globe } from "@phosphor-icons/react";
import Logo from "@/components/Logo";

export default function Landing() {
  const nav = useNavigate();
  return (
    <div className="min-h-screen bg-[--paper] text-[--ink]">
      {/* Header */}
      <header className="border-b border-[--line]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-5 flex items-center justify-between">
          <Link to="/" data-testid="brand-link" className="flex items-center">
            <Logo className="h-9" />
          </Link>
          <nav className="hidden md:flex items-center gap-8">
            <a href="#workflow" className="text-sm hover:text-[--accent] transition-colors">Workflow</a>
            <a href="#features" className="text-sm hover:text-[--accent] transition-colors">Features</a>
            <a href="#dialects" className="text-sm hover:text-[--accent] transition-colors">Dialects</a>
          </nav>
          <div className="flex items-center gap-3">
            <Link to="/login" data-testid="login-link" className="text-sm font-semibold hover:text-[--accent]">Sign in</Link>
            <Link
              to="/login"
              data-testid="get-started-btn"
              className="bg-[--accent] text-white px-4 py-2 text-sm font-semibold hover:bg-[--accent-hi] transition-colors"
            >
              Sign in
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative border-b border-[--line] overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            <div className="overline text-[--muted] mb-6">SRT&nbsp;/&nbsp;v1.0&nbsp;/&nbsp;AR&nbsp;EN&nbsp;ES&nbsp;FR&nbsp;…</div>
            <h1 className="font-display font-black text-5xl sm:text-6xl lg:text-7xl xl:text-8xl tracking-tighter leading-[0.92]">
              Speech
              <br />
              customized
              <br />
              <span className="text-[--accent]">for your</span>
              <br />
              business.
            </h1>
            <p className="mt-8 text-lg text-[--muted] max-w-xl leading-relaxed">
              Flowscribe transcribes <span className="font-mono text-[--ink]">.mp3 / .wav / .m4a / .webm</span>{" "}
              into precise SRT files, then lets you translate <em>or</em> re-cast the same language into a different
              dialect — Egyptian → Modern Standard Arabic, Brazilian → European Portuguese, and so on.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <button
                onClick={() => nav("/login")}
                data-testid="hero-cta-start"
                className="bg-[--accent] text-white px-6 py-4 text-sm font-bold inline-flex items-center gap-3 hover:bg-[--accent-hi] transition-colors"
              >
                Sign in <ArrowRight weight="bold" size={16} />
              </button>
              <Link
                to="/login"
                data-testid="hero-cta-signin"
                className="border border-[--ink] px-6 py-4 text-sm font-bold hover:bg-[--accent] hover:text-white hover:border-[--accent] transition-colors"
              >
                I have an account
              </Link>
            </div>
            <div className="mt-12 grid grid-cols-3 gap-6 max-w-lg">
              <Stat k="27+" v="languages" />
              <Stat k="50+" v="dialects" />
              <Stat k="1 GB" v="per file" />
            </div>
          </div>

          {/* Hero tile / mock panel */}
          <div className="lg:col-span-5 relative">
            <div className="hard-border bg-white p-0">
              <div className="flex items-center justify-between px-5 py-3 border-b border-[--accent] bg-[--accent] text-white">
                <span className="overline text-white/80">Live&nbsp;preview</span>
                <span className="font-mono text-xs">flowscribe.srt</span>
              </div>
              <div className="grid grid-cols-2 divide-x divide-[--line]">
                <SRTMockCol dir="rtl" label="ORIGINAL — EG" lines={[
                  ["00:00:01,200", "ازيك يا صاحبي، عامل ايه النهاردة؟"],
                  ["00:00:04,600", "انا تمام الحمد لله، انت ايه اخبارك؟"],
                  ["00:00:08,100", "كل حاجة تمام، شغل ودراسة."],
                ]} arabic />
                <SRTMockCol label="MSA" dir="rtl" lines={[
                  ["00:00:01,200", "كيف حالك يا صديقي، كيف أمسيت اليوم؟"],
                  ["00:00:04,600", "أنا بخير والحمد لله، وأنت كيف حالك؟"],
                  ["00:00:08,100", "كل شيء على ما يرام، عمل ودراسة."],
                ]} arabic highlight />
              </div>
              <div className="flex items-center justify-between border-t border-[--line] px-5 py-3 bg-[--paper]">
                <span className="font-mono text-xs text-[--muted]">CONVERT · Egyptian → MSA</span>
                <span className="inline-flex items-center gap-2 font-mono text-xs">
                  <span className="w-2 h-2 bg-emerald-500"></span>READY
                </span>
              </div>
            </div>
            <div className="absolute -z-10 -top-8 -right-8 w-full h-full bg-[--accent]/10 hidden lg:block"></div>
          </div>
        </div>
      </section>

      {/* Workflow */}
      <section id="workflow" className="border-b border-[--line]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
          <div className="overline text-[--muted] mb-4">WORKFLOW</div>
          <h2 className="font-display font-black text-4xl sm:text-5xl tracking-tighter mb-12">Four steps. Zero friction.</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-[--ink]/15 border border-[--line]">
            <Step num="01" icon={<CloudArrowUp weight="duotone" size={32} />} title="Upload audio" desc="Drop any mp3, wav, m4a, or webm — up to 1 GB. TV episodes are chunked with ffmpeg and transcribed in the background." />
            <Step num="02" icon={<Waveform weight="duotone" size={32} />} title="Flowscribe transcribes" desc="Pick the source language. Our STT engines convert speech to text with segment timestamps." />
            <Step num="03" icon={<Translate weight="duotone" size={32} />} title="Translate or reshape" desc="Send the SRT to Flowscribe-GPEngine. Pick another language or another dialect of the same one." />
            <Step num="04" icon={<FileText weight="duotone" size={32} />} title="Export SRT" desc="Download a clean, numbered, timestamped .srt for YouTube, Premiere, or CapCut." />
          </div>
        </div>
      </section>

      {/* Dialects */}
      <section id="dialects" className="border-b border-[--line] bg-[--sky]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="overline text-[--accent] mb-4">DIALECT LAB</div>
            <h2 className="font-display font-black text-4xl sm:text-5xl tracking-tighter leading-[0.95]">
              Same language.
              <br />
              Different voice.
            </h2>
            <p className="mt-6 text-[--ink]/75 text-lg leading-relaxed">
              Dialect conversion isn't a translation — it's a rewrite. Flowscribe preserves your original
              meaning and timing while adjusting vocabulary, idioms, and register to match the target
              dialect natively.
            </p>
          </div>
          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-px bg-[--ink]/15">
            <DialectCard from="Egyptian" to="MSA" flag="AR" />
            <DialectCard from="Moroccan" to="MSA" flag="AR" />
            <DialectCard from="Brazilian" to="European" flag="PT" />
            <DialectCard from="Latin-American" to="Castilian" flag="ES" />
            <DialectCard from="US English" to="British" flag="EN" />
            <DialectCard from="Mandarin (Simp.)" to="Cantonese" flag="ZH" />
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="border-b border-[--line]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 grid md:grid-cols-3 gap-px bg-[--ink]/15 border border-[--line]">
          <Feature icon={<Globe weight="duotone" size={28} />} title="RTL ready" desc="Arabic, Hebrew, Persian and Urdu render natively. No awkward flipping." />
          <Feature icon={<Waveform weight="duotone" size={28} />} title="Chunked uploads" desc="Files bigger than 25MB are split, transcribed in parallel, then stitched back together." />
          <Feature icon={<FileText weight="duotone" size={28} />} title="Standards compliant" desc="Outputs standard .srt with proper numbering and HH:MM:SS,mmm timestamps." />
        </div>
      </section>

      {/* Footer */}
      <footer>
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            <Logo className="h-7" />
            <span className="text-xs text-[--muted] italic">Speech customized for your business</span>
            <span className="text-xs text-[--muted]">© {new Date().getFullYear()}</span>
          </div>
          <div className="font-mono text-xs text-[--muted]">4 STT engines · Flowscribe-WEngine · Flowscribe-EEngine · Flowscribe-GEngine · Flowscribe-AEngine · Flowscribe-GPEngine</div>
        </div>
      </footer>
    </div>
  );
}

function Stat({ k, v }) {
  return (
    <div>
      <div className="font-display font-black text-4xl tracking-tighter">{k}</div>
      <div className="overline text-[--muted] mt-1">{v}</div>
    </div>
  );
}

function Step({ num, icon, title, desc }) {
  return (
    <div className="bg-white p-8 hover:bg-[--accent] hover:text-white group transition-colors">
      <div className="flex items-start justify-between mb-8">
        <div className="font-mono text-xs text-[--muted] group-hover:text-white/70">{num}</div>
        <div className="text-[--accent] group-hover:text-white">{icon}</div>
      </div>
      <h3 className="font-display text-2xl font-bold tracking-tight mb-3">{title}</h3>
      <p className="text-sm text-[--muted] group-hover:text-white/80 leading-relaxed">{desc}</p>
    </div>
  );
}

function Feature({ icon, title, desc }) {
  return (
    <div className="bg-white p-10">
      <div className="mb-5 text-[--accent]">{icon}</div>
      <h3 className="font-display text-xl font-bold mb-2">{title}</h3>
      <p className="text-sm text-[--muted] leading-relaxed">{desc}</p>
    </div>
  );
}

function DialectCard({ from, to, flag }) {
  return (
    <div className="bg-white p-6 hover:bg-[--accent] hover:text-white transition-colors group">
      <div className="overline text-[--muted] group-hover:text-white/70 mb-3">{flag}</div>
      <div className="flex items-baseline gap-3 font-display font-bold text-lg">
        <span>{from}</span>
        <ArrowRight weight="bold" size={16} />
        <span>{to}</span>
      </div>
    </div>
  );
}

function SRTMockCol({ label, lines, dir, arabic, highlight }) {
  return (
    <div className={`p-5 ${highlight ? "bg-[--paper]" : "bg-white"}`}>
      <div className="overline text-[--muted] mb-4">{label}</div>
      <div className="space-y-4" dir={dir || "ltr"}>
        {lines.map(([t, txt], i) => (
          <div key={`${t}-${i}`}>
            <div className="font-mono text-[10px] text-[--signal]">{t}</div>
            <div className={`${arabic ? "font-arabic" : ""} text-sm leading-snug`}>{txt}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
